'use client';

import { createClient } from '@/lib/supabase/client';

export interface SavedFilter {
  id: string;
  user_id: string;
  filter_name: string;
  micro_market?: string;
  project_type?: string;
  min_price_sqft?: number;
  max_price_sqft?: number;
  min_absorption_pct?: number;
  max_absorption_pct?: number;
  project_status?: string;
  email_alerts_enabled: boolean;
  last_notified_at?: string;
  created_at: string;
  updated_at: string;
}

export interface PriceThresholdAlert {
  id: string;
  user_id: string;
  project_id: string;
  threshold_type: 'above' | 'below';
  price_per_sqft_threshold: number;
  is_triggered: boolean;
  triggered_at?: string;
  email_alerts_enabled: boolean;
  created_at: string;
  projects?: {
    id: string;
    project_name: string;
    developer_name: string;
    price_per_sqft: number;
    slug: string;
    micro_market: string;
  };
}

export interface CreateSavedFilterInput {
  filter_name: string;
  micro_market?: string;
  project_type?: string;
  min_price_sqft?: number;
  max_price_sqft?: number;
  min_absorption_pct?: number;
  max_absorption_pct?: number;
  project_status?: string;
  email_alerts_enabled?: boolean;
}

export interface CreatePriceThresholdInput {
  project_id: string;
  threshold_type: 'above' | 'below';
  price_per_sqft_threshold: number;
}

const supabase = createClient();

export const savedFiltersService = {
  async getSavedFilters(userId: string): Promise<SavedFilter[]> {
    const { data, error } = await supabase
      .from('saved_filters')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[savedFiltersService.getSavedFilters]', error.message);
      return [];
    }
    return (data as SavedFilter[]) || [];
  },

  async createSavedFilter(userId: string, input: CreateSavedFilterInput): Promise<SavedFilter | null> {
    const { data, error } = await supabase
      .from('saved_filters')
      .insert({ user_id: userId, ...input })
      .select()
      .single();

    if (error) {
      console.error('[savedFiltersService.createSavedFilter]', error.message);
      return null;
    }
    return data as SavedFilter;
  },

  async updateSavedFilter(filterId: string, updates: Partial<CreateSavedFilterInput>): Promise<boolean> {
    const { error } = await supabase
      .from('saved_filters')
      .update(updates)
      .eq('id', filterId);

    if (error) {
      console.error('[savedFiltersService.updateSavedFilter]', error.message);
      return false;
    }
    return true;
  },

  async deleteSavedFilter(filterId: string): Promise<boolean> {
    const { error } = await supabase
      .from('saved_filters')
      .delete()
      .eq('id', filterId);

    if (error) {
      console.error('[savedFiltersService.deleteSavedFilter]', error.message);
      return false;
    }
    return true;
  },

  async toggleFilterAlerts(filterId: string, enabled: boolean): Promise<boolean> {
    const { error } = await supabase
      .from('saved_filters')
      .update({ email_alerts_enabled: enabled })
      .eq('id', filterId);

    if (error) {
      console.error('[savedFiltersService.toggleFilterAlerts]', error.message);
      return false;
    }
    return true;
  },

  async getPriceThresholdAlerts(userId: string): Promise<PriceThresholdAlert[]> {
    const { data, error } = await supabase
      .from('price_threshold_alerts')
      .select(`
        *,
        projects (
          id,
          project_name,
          developer_name,
          price_per_sqft,
          slug,
          micro_market
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[savedFiltersService.getPriceThresholdAlerts]', error.message);
      return [];
    }
    return (data as PriceThresholdAlert[]) || [];
  },

  async createPriceThresholdAlert(userId: string, input: CreatePriceThresholdInput): Promise<PriceThresholdAlert | null> {
    const { data, error } = await supabase
      .from('price_threshold_alerts')
      .insert({ user_id: userId, ...input })
      .select(`
        *,
        projects (
          id,
          project_name,
          developer_name,
          price_per_sqft,
          slug,
          micro_market
        )
      `)
      .single();

    if (error) {
      console.error('[savedFiltersService.createPriceThresholdAlert]', error.message);
      return null;
    }
    return data as PriceThresholdAlert;
  },

  async deletePriceThresholdAlert(alertId: string): Promise<boolean> {
    const { error } = await supabase
      .from('price_threshold_alerts')
      .delete()
      .eq('id', alertId);

    if (error) {
      console.error('[savedFiltersService.deletePriceThresholdAlert]', error.message);
      return false;
    }
    return true;
  },

  async toggleThresholdAlerts(alertId: string, enabled: boolean): Promise<boolean> {
    const { error } = await supabase
      .from('price_threshold_alerts')
      .update({ email_alerts_enabled: enabled })
      .eq('id', alertId);

    if (error) {
      console.error('[savedFiltersService.toggleThresholdAlerts]', error.message);
      return false;
    }
    return true;
  },

  async resetTriggeredAlert(alertId: string): Promise<boolean> {
    const { error } = await supabase
      .from('price_threshold_alerts')
      .update({ is_triggered: false, triggered_at: null })
      .eq('id', alertId);

    if (error) {
      console.error('[savedFiltersService.resetTriggeredAlert]', error.message);
      return false;
    }
    return true;
  },
};
